import { type History, createBrowserHistory } from 'history'
import mitt from 'mitt'
import { AppRoutes } from '@core/AppRoutes'
import { KAMBI_ROOT_ROUTE } from '@core/Areas/KambiClient/constants'
import { getKambiSportsOfferingIntegrationOverrideSetting } from '@core/Areas/Settings/Store/selectors'
import { getFeatureValue } from '@core/State/FeatureFlags/helpers/getFeatureValue'
import { store } from '@core/Store'

export const appHistory = createBrowserHistory() as History

/** Used to listen for Kambi internal route changes */
export const browserHistoryEmitter = mitt<{ pushState: string }>()

// =============
// Local Helpers
// =============

const pushState = (url: string) => browserHistoryEmitter.emit('pushState', url)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PushStateArgs = [data: any, unused: string, url?: string | URL | null]

if (window.history) {
  // Only necessary while Kambi's internal router replaces mobi home URL with kambi home URL
  window.history.replaceState = new Proxy(window.history.replaceState, {
    apply: (target, thisArg, argArray: PushStateArgs) => {
      const [data, , url] = argArray

      const shouldForce = data?.usr?.force ?? false
      const destination = url?.toString() || location.pathname

      // Ignore kambi attempting to modify history when NOT in kambi route
      if (!shouldForce && destination.includes(KAMBI_ROOT_ROUTE) && location.pathname === '/') {
        return
      }

      // When NEW sports offering feature is active, ignore kambi routing side-effects
      if (
        (destination.includes(KAMBI_ROOT_ROUTE + '/sports') ||
          destination.includes(KAMBI_ROOT_ROUTE + '/home')) &&
        getFeatureValue('kambi-sports-offering-integration') &&
        getKambiSportsOfferingIntegrationOverrideSetting(store.getState())
      ) {
        return
      }

      return target.apply(thisArg, argArray)
    },
  })

  // Only necessary while Kambi's internal router replaces current with the same
  window.history.pushState = new Proxy(window.history.pushState, {
    apply: (target, thisArg: History, argArray: PushStateArgs) => {
      const [, , url] = argArray

      const destination = url?.toString() || location.pathname

      // Intercept kambi event navigation when new sports feature is active
      if (
        destination.includes(KAMBI_ROOT_ROUTE) &&
        destination.startsWith(`/${KAMBI_ROOT_ROUTE}/event/`) &&
        getFeatureValue('kambi-sports-offering-integration') &&
        getKambiSportsOfferingIntegrationOverrideSetting(store.getState())
      ) {
        const newRoute = buildNewSportsEventPathFromKambiEventPath(destination)
        pushState(newRoute)
        target.apply(thisArg, [undefined, '', newRoute])
        const event = new PopStateEvent('popstate')
        window.dispatchEvent(event)
        return
      }

      if (destination.includes(KAMBI_ROOT_ROUTE) && location.pathname === destination) {
        // eslint-disable-next-line no-console
        console.warn(`Ignored pushState for ${destination}. We are not inside kambi`)
        return
      }

      // Ignore kambi home navigation when new sports feature is active
      if (
        destination.includes(KAMBI_ROOT_ROUTE + '/home') &&
        getFeatureValue('kambi-sports-offering-integration') &&
        getKambiSportsOfferingIntegrationOverrideSetting(store.getState())
      ) {
        return
      }

      pushState(destination)
      return target.apply(thisArg, argArray)
    },
  })
}

function buildNewSportsEventPathFromKambiEventPath(destination: string) {
  const eventNumber = destination.match(/\d+/)?.[0]
  if (eventNumber) {
    return AppRoutes.Sports + `/event/${eventNumber}`
  }
  return AppRoutes.Sports
}
